// Mock of the Job's Invoice & payment panel (ADR-025), so the panel can be looked at before it ships — the
// invoice-by-line build of 5 November 2026 went out unseen and kept two invoice buttons. Three accepted lines
// shaped like a real vinyl Job (dispose, levelling, installation) on a generic test customer; the issue command
// records invoices in memory with the line keys it was given, as the backbone does.
import type { ProposalResult } from "@artemis-group/aiw-contracts";

type Inv = { id: string; xero_invoice_id: string; invoice_number: string; status: string; subtotal: number; gst: number; total: number; amount_paid: number; amount_due: number; online_url: null; created_at: string; line_keys: string[] | null; work: string[] };
const LINES = [
  { key: "service:remove-carpet", label: "Lift, remove & dispose of existing carpet", sell: 240, included: false },
  { key: "service:levelling", label: "Floor levelling - Flush to kitchen tiles", sell: 3600, included: false },
  { key: "service:install", label: "Professional vinyl installation, design & project management", sell: 3750, included: false },
];
const AGREED = 7590, GST_RATE = 0.1;
const invoices = new Map<string, Inv[]>();
const r2 = (n: number) => Math.round(n * 100) / 100;

function state(ref: string) {
  const list = invoices.get(ref) ?? [];
  const on = new Map<string, string>();
  for (const i of list) for (const k of i.line_keys ?? []) on.set(k, i.invoice_number);
  const invoiced = r2(list.reduce((n, i) => n + i.subtotal, 0));
  const received = r2(list.reduce((n, i) => n + i.amount_paid, 0));
  return {
    job: { name: "Test customer — Turramurra", test_set_id: null }, detail: { payment_method: "TBC" },
    subtotal: AGREED, gst: r2(AGREED * GST_RATE), total: r2(AGREED * (1 + GST_RATE)), gst_rate: GST_RATE,
    invoice: list.at(-1) ?? null, invoices: list,
    lines: LINES.map((l) => ({ ...l, invoice_number: on.get(l.key) ?? null })),
    split_available: !list.some((i) => !i.line_keys) && LINES.some((l) => !on.has(l.key)),
    invoiced_ex_gst: invoiced, uninvoiced_ex_gst: r2(AGREED - invoiced),
    requests: [], open: [], received, received_source: "job", recorded: 0, balance: r2(AGREED * (1 + GST_RATE) - received),
    cash_ledger: false, aiw_origin: true, source_key: "0".repeat(64),
  };
}

export function paymentRequestsProposeMock(command: string, args: Record<string, unknown>, action_id: string): ProposalResult | null {
  const ref = String(args.job_ref ?? "");
  if (command === "inspect_payment_requests") return { status: "executed", action_id, result: state(ref) } as ProposalResult;
  if (command === "issue_job_invoice") {
    const list = invoices.get(ref) ?? [];
    const keys = Array.isArray(args.line_keys) ? (args.line_keys as string[]) : null;
    const done = new Set(list.flatMap((i) => i.line_keys ?? []));
    const chosen = keys ?? LINES.filter((l) => !done.has(l.key)).map((l) => l.key);
    const subtotal = keys || list.length ? r2(LINES.filter((l) => chosen.includes(l.key)).reduce((n, l) => n + l.sell, 0)) : AGREED;
    const n = 96 + [...invoices.values()].flat().length;
    const inv: Inv = { id: crypto.randomUUID(), xero_invoice_id: `x-${n}`, invoice_number: `INV-00${n}`, status: "AUTHORISED", subtotal, gst: r2(subtotal * GST_RATE), total: r2(subtotal * (1 + GST_RATE)), amount_paid: 0, amount_due: r2(subtotal * (1 + GST_RATE)), online_url: null, created_at: new Date().toISOString(),
      line_keys: keys || list.length ? chosen : null, work: LINES.filter((l) => chosen.includes(l.key)).map((l) => l.label) };
    invoices.set(ref, [...list, inv]);
    return { status: "executed", action_id, result: { invoice: inv, issued: true } } as ProposalResult;
  }
  if (command === "void_job_invoice") {
    const list = invoices.get(ref) ?? [], target = args.invoice_id ?? list.at(-1)?.id; // the named invoice, else the only one
    invoices.set(ref, list.filter((i) => i.id !== target));
    return { status: "executed", action_id, result: { voided: true } } as ProposalResult;
  }
  return null;
}
